import type {
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
} from '@angular/forms';

export type WeightableFormGroup = FormGroup<{
  weight: FormControl<number>;
}>;

export class WeightsValidator {
  /**
   * Verifies that all children that have a weight attribute add up to 100.
   */
  static full(array: FormArray<WeightableFormGroup>): ValidationErrors | null {
    const sum = array.value.reduce((acc, curr) => {
      if (Number.isNaN(Number(curr.weight))) {
        return acc;
      }

      return acc + Number(curr.weight);
    }, 0);

    if (!array.value?.length) {
      return null;
    }

    if (sum !== 100) {
      return { full: { valid: false } };
    }

    return null;
  }

  static positive(
    array: FormArray<WeightableFormGroup>,
  ): ValidationErrors | null {
    if (!array?.value.length) {
      return null;
    }

    const hasNegative =
      array.value.map((item) => item.weight ?? 0).filter((weight) => weight < 0)
        .length > 0;

    if (hasNegative) {
      return { positive: { valid: false } };
    }

    return null;
  }

  static range(array: FormArray<WeightableFormGroup>): ValidationErrors | null {
    if (!array?.value.length) {
      return null;
    }

    const outOfRange =
      array.value
        .map((item) => item.weight ?? 0)
        .filter((weight) => weight < 0 || weight > 100).length > 0;

    if (outOfRange) {
      return { range: { valid: false } };
    }

    return null;
  }

  static sum(array: FormArray<WeightableFormGroup>): ValidationErrors | null {
    const notEqual =
      array.value.reduce((a, b) => a + Number(b.weight), 0) !== 100;

    if (notEqual) {
      return { sum: { valid: false } };
    }

    return null;
  }
}
